import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar.js';
import { MobileDrawer } from '@/components/layout/MobileDrawer.js';
import { MobileDrawerContent } from '@/components/layout/MobileDrawerContent.js';
import { ConversationsPanel } from '@/components/panels/ConversationsPanel.js';
import { InboxPanel } from '@/components/panels/InboxPanel.js';
import { ContactsPanel } from '@/components/panels/ContactsPanel.js';
import { SchedulesPanel } from '@/components/panels/SchedulesPanel.js';
// DISABLED: Settings modal (model routing not offered yet)
// import { SettingsModal } from '@/components/settings/SettingsModal.js';
import { MemoriesOverlay } from '@/components/overlays/MemoriesOverlay.js';
import { PromptsOverlay } from '@/components/overlays/PromptsOverlay.js';
import { ScheduleDetailOverlay } from '@/components/scheduler/ScheduleDetailOverlay.js';
import { ResizableSplit } from '@/components/layout/ResizableSplit.js';
import { ArtifactPanel } from '@/components/artifact/ArtifactPanel.js';
import { Toaster } from '@/components/common/Toaster.js';
import { useMobile } from '@/hooks/useMobile.js';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts.js';
import { useUserEvents } from '@/hooks/useUserEvents.js';
import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';
import { Button } from '@/components/ui/button.js';
import { Menu } from 'lucide-react';

export function AppLayout() {
  const isMobile = useMobile();
  // Desktop sidebar panel state (not used on mobile)
  const [activePanel, setActivePanel] = useState<'conversations' | 'inbox' | 'contacts' | 'schedules' | 'notes' | null>(null);
  const [activeOverlay, setActiveOverlay] = useState<'notes' | 'memories' | 'prompts' | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isOpen: artifactPanelOpen, splitRatio, setSplitRatio, layoutOrientation } = useArtifactPanelStore();

  // Enable global keyboard shortcuts
  useKeyboardShortcuts();

  // Enable real-time event subscriptions (scheduler events, etc.)
  useUserEvents();

  const handleViewScheduleDetails = (scheduleId: string) => {
    setSelectedScheduleId(scheduleId);
  };

  const handleDrawerSelect = () => {
    setDrawerOpen(false);
  };

  return (
    <div className="flex h-screen w-full bg-background overflow-hidden">
      {/* Desktop: Sidebar with panels */}
      {!isMobile && (
        <Sidebar
          activePanel={activePanel === 'notes' ? null : activePanel}
          onPanelChange={setActivePanel}
          activeOverlay={activeOverlay}
          onOverlayChange={setActiveOverlay}
        >
          {activePanel === 'conversations' && <ConversationsPanel />}
          {activePanel === 'inbox' && <InboxPanel />}
          {activePanel === 'contacts' && <ContactsPanel />}
          {activePanel === 'schedules' && <SchedulesPanel onViewDetails={handleViewScheduleDetails} />}
        </Sidebar>
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Mobile: Floating hamburger button */}
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDrawerOpen(true)}
            className="absolute top-3 left-3 z-50 h-10 w-10 rounded-lg bg-background/80 backdrop-blur-sm border border-border/50 shadow-md hover:bg-background"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Main content - Chat or Welcome page */}
        <div className="absolute inset-0 flex flex-col">
          {/* Desktop: Show split panel when artifact panel is open */}
          {!isMobile && artifactPanelOpen ? (
            <ResizableSplit
              left={layoutOrientation === 'left' ? <ArtifactPanel /> : <Outlet />}
              right={layoutOrientation === 'left' ? <Outlet /> : <ArtifactPanel />}
              splitRatio={splitRatio}
              onSplitRatioChange={setSplitRatio}
              minRatio={0.3}
              maxRatio={0.7}
            />
          ) : (
            <Outlet />
          )}
        </div>

        {/* Overlays slide over the main content */}
        {activeOverlay === 'memories' && <MemoriesOverlay />}
        {activeOverlay === 'prompts' && <PromptsOverlay onClose={() => setActiveOverlay(null)} />}
        {selectedScheduleId && (
          <ScheduleDetailOverlay
            scheduleId={selectedScheduleId}
            onClose={() => setSelectedScheduleId(null)}
          />
        )}

        <Toaster />
      </div>

      {/* Mobile: Side drawer with Chats and Routines */}
      {isMobile && (
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <MobileDrawerContent
            onSelect={handleDrawerSelect}
            onClose={() => setDrawerOpen(false)}
            onViewScheduleDetails={handleViewScheduleDetails}
          />
        </MobileDrawer>
      )}

      {/* DISABLED: Settings modal (model routing not offered yet) */}
    </div>
  );
}
